import type { ColumnsType } from 'antd/es/table';
import { createElement } from 'react';

import {
    getSelectedTableColumns,
    getSystemColumnItems,
    isTableVisibleCatalogItem,
} from '../ScenarioLensSection/scenarioLensCatalog.helpers';
import type { LensCatalogData } from '../ScenarioLensSection/scenarioLensCatalog.types';
import AiColumnHeader from './AiColumnHeader';
import { renderSuggestionTile } from './SuggestionTile';

export type LensTableRow = Record<string, unknown>;

export type BuildLensTableColumnsResult = {
    columns: ColumnsType<LensTableRow>;
    hasSelection: boolean;
    hasRowSelection: boolean;
};

/** AI system column first (frozen), then dimensions, then KPI groups (Current / Chg +/- / New). */
export const buildLensTableColumns = (catalog: LensCatalogData): BuildLensTableColumnsResult => {
    const { dimensions, kpis } = getSelectedTableColumns(catalog);
    const selectedSystemColumns = getSystemColumnItems(catalog).filter(isTableVisibleCatalogItem);
    const hasFrozenLeftColumns = selectedSystemColumns.length > 0 || dimensions.length > 0;

    const systemColumns: ColumnsType<LensTableRow> = selectedSystemColumns.map((item) => ({
        title: createElement(AiColumnHeader),
        dataIndex: item.attribute_key,
        key: item.attribute_key,
        width: 100,
        fixed: 'left' as const,
        className: 'lens-table-ai-column',
        render: (value: unknown) => renderSuggestionTile(value),
    }));

    const dimensionColumns: ColumnsType<LensTableRow> = dimensions.map((item) => ({
        title: item.display_name,
        dataIndex: item.attribute_key,
        key: item.attribute_key,
        width: 160,
        fixed: hasFrozenLeftColumns ? ('left' as const) : undefined,
    }));

    const kpiColumns: ColumnsType<LensTableRow> = kpis.map((item) => ({
        title: item.display_name,
        key: item.attribute_key,
        children: [
            {
                title: 'Current',
                dataIndex: `${item.attribute_key}_current`,
                key: `${item.attribute_key}_current`,
                width: 110,
                align: 'right' as const,
            },
            {
                title: 'Chg +/-',
                dataIndex: `${item.attribute_key}_chg`,
                key: `${item.attribute_key}_chg`,
                width: 90,
                align: 'center' as const,
            },
            {
                title: 'New',
                dataIndex: `${item.attribute_key}_new`,
                key: `${item.attribute_key}_new`,
                width: 110,
                align: 'right' as const,
            },
        ],
    }));

    return {
        columns: [...systemColumns, ...dimensionColumns, ...kpiColumns],
        hasSelection: dimensions.length > 0 || kpis.length > 0,
        hasRowSelection: selectedSystemColumns.length > 0,
    };
};
